import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Anchor } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { authApi } from '@modules/auth/api/auth.api';
import { formatPickupPhoneForInput } from '@modules/pickup-points/utils/formatPickupPhone';
import { Alert, Button, Checkbox, Input } from '@shared/ui';
import { authUtils } from '@shared/utils/axios-instance';
import { clearSellerClientState } from '@shared/utils/clear-seller-client-state';
import {
  sellerAdvertisingConsentUrl,
  sellerHelpUrl,
  sellerPrivacyPolicyUrl,
} from '@shared/utils/seller-help-url';
import brandText from '../../assets/text.png';
import logoMark from '../../assets/logo-text-empty.png';
import mascot from '../../assets/maskot.png';
import './SellerPhoneLoginPage.css';

function toApiPhone(raw: string): string {
  const d = raw.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('8')) {
    return `+7${d.slice(1)}`;
  }
  if (d.length === 10) {
    return `+7${d}`;
  }
  if (d.length === 11 && d.startsWith('7')) {
    return `+${d}`;
  }
  if (raw.trim().startsWith('+') && d.length > 0) {
    return `+${d}`;
  }
  return d ? `+${d}` : '';
}

const PHONE_RE = /^\+?[1-9]\d{1,14}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CODE_LENGTH = 4;
/** Совпадает с AuthService.SEND_CODE_COOLDOWN_SEC на бэке */
const SEND_CODE_COOLDOWN_SEC = 60;

type LoginMethod = 'phone' | 'email';

type AuthErrorBody = {
  code?: string;
  message?: string | { code?: string; message?: string; retryAfterSec?: number };
  retryAfterSec?: number;
};

function readSendCodeCooldown(data: AuthErrorBody | undefined): number | null {
  if (!data) return null;
  const nested =
    data.message && typeof data.message === 'object' ? data.message : null;
  const code = data.code || nested?.code;
  const retry =
    typeof data.retryAfterSec === 'number'
      ? data.retryAfterSec
      : typeof nested?.retryAfterSec === 'number'
        ? nested.retryAfterSec
        : null;
  const message =
    typeof data.message === 'string' ? data.message : nested?.message;

  if (code === 'SEND_CODE_TOO_OFTEN' || retry != null) {
    if (retry != null && retry > 0) return Math.ceil(retry);
    const m = message?.match(/через\s+(\d+)/i);
    if (m) return Number(m[1]);
  }
  return null;
}

function readErrorMessage(data: AuthErrorBody | undefined, fallback: string): string {
  if (!data) return fallback;
  if (typeof data.message === 'string' && data.message.trim()) return data.message;
  if (data.message && typeof data.message === 'object' && data.message.message) {
    return data.message.message;
  }
  return fallback;
}

export default function SellerPhoneLoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [method, setMethod] = useState<LoginMethod>('phone');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [digits, setDigits] = useState<string[]>(() => Array(CODE_LENGTH).fill(''));
  const [codeStep, setCodeStep] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldownSec, setCooldownSec] = useState(0);
  const [pdConsent, setPdConsent] = useState(false);
  const [adsConsent, setAdsConsent] = useState(false);
  const digitRefs = useRef<(HTMLInputElement | null)[]>([]);
  const verifyingRef = useRef(false);

  useEffect(() => {
    if (authUtils.getAccessToken()) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (cooldownSec <= 0) return;
    const t = window.setTimeout(() => {
      setCooldownSec((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearTimeout(t);
  }, [cooldownSec]);

  useEffect(() => {
    if (!codeStep || verifying) return;
    const t = window.setTimeout(() => digitRefs.current[0]?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [codeStep, verifying]);

  const switchMethod = (next: LoginMethod) => {
    if (sending || verifying || next === method) return;
    setMethod(next);
    setCodeStep(false);
    setDigits(Array(CODE_LENGTH).fill(''));
    setError(null);
    setCooldownSec(0);
  };

  const backToIdentifier = () => {
    if (verifying) return;
    setCodeStep(false);
    setDigits(Array(CODE_LENGTH).fill(''));
    setError(null);
  };

  const handleSendCodeError = (e: unknown) => {
    const data = (e as { response?: { data?: AuthErrorBody } })?.response?.data;
    const wait = readSendCodeCooldown(data);
    if (wait != null && wait > 0) {
      setCooldownSec(wait);
      setError(null);
      return;
    }
    setCooldownSec(0);
    setError(readErrorMessage(data, 'Не удалось отправить код'));
  };

  const sendCode = async () => {
    setError(null);
    if (cooldownSec > 0) return;
    if (!pdConsent) {
      setError('Нужно дать согласие на обработку персональных данных');
      return;
    }

    if (method === 'phone') {
      const normalized = toApiPhone(phone);
      if (!PHONE_RE.test(normalized)) {
        setError('Введите номер в формате +7… или 9XXXXXXXXX');
        return;
      }
      setSending(true);
      try {
        await authApi.sendPhoneCode(normalized);
        setCooldownSec(SEND_CODE_COOLDOWN_SEC);
        setDigits(Array(CODE_LENGTH).fill(''));
        setCodeStep(true);
        notifications.show({
          title: 'Код отправлен',
          message: 'Проверьте SMS',
          color: 'green',
        });
      } catch (e: unknown) {
        handleSendCodeError(e);
      } finally {
        setSending(false);
      }
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    if (!EMAIL_RE.test(normalizedEmail)) {
      setError('Введите корректный email');
      return;
    }
    setSending(true);
    try {
      await authApi.sendEmailCode(normalizedEmail);
      setCooldownSec(SEND_CODE_COOLDOWN_SEC);
      setDigits(Array(CODE_LENGTH).fill(''));
      setCodeStep(true);
      notifications.show({
        title: 'Код отправлен',
        message: 'Проверьте почту',
        color: 'green',
      });
    } catch (e: unknown) {
      handleSendCodeError(e);
    } finally {
      setSending(false);
    }
  };

  const verify = async (codeValue: string) => {
    if (verifyingRef.current) return;
    setError(null);

    if (method === 'phone') {
      const normalized = toApiPhone(phone);
      if (!PHONE_RE.test(normalized)) {
        setError('Некорректный номер');
        return;
      }
      if (codeValue.length < CODE_LENGTH) {
        setError('Введите код из SMS');
        return;
      }
      verifyingRef.current = true;
      setVerifying(true);
      try {
        const res = await authApi.verifyPhone(normalized, codeValue, adsConsent);
        await clearSellerClientState(queryClient);
        authUtils.setAccessToken(res.access_token);
        authUtils.setRefreshToken(res.refresh_token);
        navigate('/', { replace: true });
      } catch (e: unknown) {
        const msg =
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Неверный код или номер';
        setError(msg);
        setDigits(Array(CODE_LENGTH).fill(''));
        verifyingRef.current = false;
        setVerifying(false);
        window.setTimeout(() => digitRefs.current[0]?.focus(), 0);
      }
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    if (!EMAIL_RE.test(normalizedEmail)) {
      setError('Некорректный email');
      return;
    }
    if (codeValue.length < CODE_LENGTH) {
      setError('Введите код из письма');
      return;
    }
    verifyingRef.current = true;
    setVerifying(true);
    try {
      const res = await authApi.verifyEmail(normalizedEmail, codeValue, adsConsent);
      await clearSellerClientState(queryClient);
      authUtils.setAccessToken(res.access_token);
      authUtils.setRefreshToken(res.refresh_token);
      navigate('/', { replace: true });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Неверный код или email';
      setError(msg);
      setDigits(Array(CODE_LENGTH).fill(''));
      verifyingRef.current = false;
      setVerifying(false);
      window.setTimeout(() => digitRefs.current[0]?.focus(), 0);
    }
  };

  const applyDigits = (next: string[]) => {
    setDigits(next);
    const joined = next.join('');
    if (joined.length === CODE_LENGTH && next.every((d) => d !== '')) {
      void verify(joined);
    }
  };

  const onDigitChange = (index: number, raw: string) => {
    if (verifying) return;
    const onlyDigits = raw.replace(/\D/g, '');
    if (!onlyDigits) {
      const next = [...digits];
      next[index] = '';
      setDigits(next);
      return;
    }

    // вставка нескольких цифр / автозаполнение SMS
    if (onlyDigits.length > 1) {
      const next = [...digits];
      const chars = onlyDigits.slice(0, CODE_LENGTH).split('');
      chars.forEach((ch, i) => {
        if (index + i < CODE_LENGTH) next[index + i] = ch;
      });
      applyDigits(next);
      const focusAt = Math.min(index + chars.length, CODE_LENGTH - 1);
      digitRefs.current[focusAt]?.focus();
      return;
    }

    const next = [...digits];
    next[index] = onlyDigits.slice(-1);
    applyDigits(next);
    if (index < CODE_LENGTH - 1) {
      digitRefs.current[index + 1]?.focus();
    }
  };

  const onDigitKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (verifying) return;
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const next = [...digits];
        next[index] = '';
        setDigits(next);
      } else if (index > 0) {
        const next = [...digits];
        next[index - 1] = '';
        setDigits(next);
        digitRefs.current[index - 1]?.focus();
      }
      e.preventDefault();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      digitRefs.current[index - 1]?.focus();
      e.preventDefault();
    }
    if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      digitRefs.current[index + 1]?.focus();
      e.preventDefault();
    }
  };

  const phoneDigitsCount = phone.replace(/\D/g, '');
  const phoneComplete = phoneDigitsCount.length === 11;
  const emailComplete = EMAIL_RE.test(email.trim());
  const canSend =
    pdConsent &&
    !sending &&
    cooldownSec <= 0 &&
    (method === 'phone' ? phoneComplete : emailComplete);

  // На шаге кода таймер показан у кнопки «Отправить повторно», алерт не дублируем
  const displayError =
    !codeStep && cooldownSec > 0
      ? `Повторно отправить код можно через ${cooldownSec} сек.`
      : error;

  const title = !codeStep
    ? 'Вход или регистрация'
    : method === 'phone'
      ? 'Введите код из SMS'
      : 'Введите код из письма';

  const subtitle = !codeStep
    ? method === 'phone'
      ? 'Введите телефон. Если аккаунта ещё нет, вы будете перенаправлены на страницу регистрации.'
      : 'Введите email. Если аккаунта ещё нет, вы будете перенаправлены на страницу регистрации.'
    : null;

  return (
    <div className="seller-phone-login">
      <header className="seller-phone-login__top">
        <div className="seller-phone-login__hero-brand">
          <div className="seller-phone-login__hero-title-row">
            <img src={logoMark} alt="" className="seller-phone-login__hero-mark" />
            <img src={brandText} alt="Лавка цветов" className="seller-phone-login__hero-text" />
          </div>
        </div>
        <a
          href={sellerHelpUrl()}
          className="seller-phone-login__help"
          target="_blank"
          rel="noopener noreferrer"
        >
          Помощь
        </a>
      </header>

      <div className="seller-phone-login__body">
        <aside className="seller-phone-login__hero" aria-hidden>
          <img src={mascot} alt="" className="seller-phone-login__mascot" />
        </aside>

        <div className="seller-phone-login__card">
          <div className="seller-phone-login__card-brand">
            <div>
              <p className="seller-phone-login__eyebrow">Кабинет продавца</p>
              <h1 className="seller-phone-login__title">{title}</h1>
              {subtitle ? <p className="seller-phone-login__subtitle">{subtitle}</p> : null}
            </div>
          </div>

          {!codeStep ? (
            <div className="seller-phone-login__method-tabs" role="tablist" aria-label="Способ входа">
              <button
                type="button"
                role="tab"
                aria-selected={method === 'phone'}
                className={
                  method === 'phone'
                    ? 'seller-phone-login__method-tab seller-phone-login__method-tab--active'
                    : 'seller-phone-login__method-tab'
                }
                onClick={() => switchMethod('phone')}
                disabled={sending}
              >
                Телефон
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={method === 'email'}
                className={
                  method === 'email'
                    ? 'seller-phone-login__method-tab seller-phone-login__method-tab--active'
                    : 'seller-phone-login__method-tab'
                }
                onClick={() => switchMethod('email')}
                disabled={sending}
              >
                Email
              </button>
            </div>
          ) : null}

          {displayError ? (
            <Alert tone="error" mb="md">
              {displayError}
            </Alert>
          ) : null}

          {!codeStep ? (
            <div className="seller-phone-login__form">
              {method === 'phone' ? (
                <Input
                  className="seller-phone-login__field"
                  label="Телефон"
                  placeholder="+7 (900) 000-00-00"
                  type="tel"
                  inputMode="tel"
                  radius="md"
                  size="md"
                  value={phone}
                  onChange={(e) => setPhone(formatPickupPhoneForInput(e.currentTarget.value))}
                  disabled={sending}
                />
              ) : (
                <Input
                  className="seller-phone-login__field"
                  label="Email"
                  placeholder="email@example.com"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  radius="md"
                  size="md"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  disabled={sending}
                />
              )}
              <div className="seller-phone-login__consents">
                <Checkbox
                  size="sm"
                  color="pink"
                  checked={pdConsent}
                  onChange={(e) => setPdConsent(e.currentTarget.checked)}
                  disabled={sending}
                  label={
                    <>
                      Я даю{' '}
                      <Anchor
                        size="sm"
                        href={sellerPrivacyPolicyUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="seller-phone-login__link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        согласие на обработку персональных данных
                      </Anchor>
                    </>
                  }
                />
                <Checkbox
                  size="sm"
                  color="pink"
                  checked={adsConsent}
                  onChange={(e) => setAdsConsent(e.currentTarget.checked)}
                  disabled={sending}
                  label={
                    <>
                      Я даю{' '}
                      <Anchor
                        size="sm"
                        href={sellerAdvertisingConsentUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="seller-phone-login__link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        согласие на получение рекламной рассылки
                      </Anchor>
                    </>
                  }
                />
              </div>
              <Button
                variant="primary"
                fullWidth
                loading={sending}
                onClick={sendCode}
                disabled={!canSend}
              >
                {cooldownSec > 0
                  ? `Повторно через ${cooldownSec} сек.`
                  : 'Получить код'}
              </Button>
            </div>
          ) : (
            <div className="seller-phone-login__form">
              <p className="seller-phone-login__code-hint">
                Код отправлен на {method === 'phone' ? phone : email.trim().toLowerCase()}
              </p>
              <div
                className="seller-phone-login__otp"
                role="group"
                aria-label={method === 'phone' ? 'Код из SMS' : 'Код из письма'}
              >
                {digits.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      digitRefs.current[index] = el;
                    }}
                    className="seller-phone-login__otp-digit"
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    maxLength={index === 0 ? CODE_LENGTH : 1}
                    value={digit}
                    disabled={verifying}
                    aria-label={`Цифра ${index + 1}`}
                    onChange={(e) => onDigitChange(index, e.currentTarget.value)}
                    onKeyDown={(e) => onDigitKeyDown(index, e)}
                    onFocus={(e) => e.currentTarget.select()}
                  />
                ))}
              </div>
              {verifying ? (
                <p className="seller-phone-login__code-status">Проверяем код…</p>
              ) : null}
              <button
                type="button"
                className="seller-phone-login__resend"
                onClick={() => void sendCode()}
                disabled={sending || verifying || cooldownSec > 0}
              >
                {sending
                  ? 'Отправляем…'
                  : cooldownSec > 0
                    ? `Отправить код повторно через ${cooldownSec} сек.`
                    : 'Отправить код повторно'}
              </button>
              <button
                type="button"
                className="seller-phone-login__back"
                onClick={backToIdentifier}
                disabled={verifying || sending}
              >
                {method === 'phone' ? 'Изменить номер' : 'Изменить email'}
              </button>
            </div>
          )}
        </div>
      </div>

      <footer className="seller-phone-login__footer">© 2026 Лавка цветов</footer>
    </div>
  );
}
